import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

const navLinks = [
  { path: '/', label: '首页' },
  { path: '/category/all', label: '分类' },
  { path: '/about', label: '关于我们' },
];

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const showTransparent = isHome && !scrolled && transparent;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 font-heading text-xl font-bold whitespace-nowrap ${
              showTransparent ? 'text-background-50' : 'text-foreground-950'
            }`}
          >
            <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-book-open-line text-background-50 text-lg"></i>
            </div>
            韩漫之家
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-primary-500'
                    : showTransparent
                      ? 'text-background-50/80 hover:text-background-50'
                      : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              className={`p-2 rounded-lg transition-colors duration-200 cursor-pointer ${
                showTransparent
                  ? 'text-background-50/80 hover:text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-200'
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/category/all"
              className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              开始阅读
            </Link>
          </div>

          <button
            className={`md:hidden p-2 rounded-lg cursor-pointer ${
              showTransparent
                ? 'text-background-50'
                : 'text-foreground-950'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/category/all"
              className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 text-center whitespace-nowrap"
            >
              开始阅读
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}